import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '../components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { useOrganizations } from '../hooks/useOrganizations'
import { AuthShell, NoticeBanner } from '../components/Auth'
import { ArchigmaMark } from '../components/Logo'
import { useAuth } from '../lib/auth'
import {
  authorizeDesktopSession,
  previewDesktopSession,
} from '../lib/endpoints/auth'
import { loreOrganizationToAuthorize } from '../lib/loreAuth'
import type { Organization } from '../lib/schemas'

interface DesktopAuthorizationParams {
  attemptId: string
  state: string
}

const PRODUCT_LABELS = {
  rendermoon_blender: 'Rendermoon dla Blender',
  archigma_rhino: 'Archigma dla Rhino',
  archigma_datasmith: 'Archigma Datasmith Exporter',
  archigma_unreal: 'Archigma dla Unreal Engine',
} as const

const SCOPE_LABELS = {
  account_read: 'odczyt konta i biezacej organizacji',
  render_submit: 'wysylanie zadan renderowania',
  asset_catalog_read: 'odczyt katalogu zasobow',
  asset_catalog_write: 'zarzadzanie katalogiem zasobow',
  analytics_policy_read: 'odczyt zasad analityki produktu',
  analytics_submit: 'wysylanie analityki aktywnosci produktu',
  release_download: 'pobieranie zatwierdzonych aktualizacji',
  geometry_export: 'chroniony eksport geometrii',
} as const

export function desktopAuthorizationParams(
  rawSearch: string,
): DesktopAuthorizationParams | null {
  const search = new URLSearchParams(rawSearch)
  const entries = Array.from(search.entries())
  const attemptId = search.get('attempt_id') ?? ''
  const state = search.get('state') ?? ''
  if (
    entries.length !== 2 ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      attemptId,
    ) ||
    state.length < 43 ||
    state.length > 128 ||
    !/^[A-Za-z0-9._~-]+$/.test(state)
  ) {
    return null
  }
  return { attemptId, state }
}

export function desktopOrganizationSelectItems(
  organizations: readonly Pick<Organization, 'id' | 'name'>[],
): Record<string, string> {
  return Object.fromEntries(
    organizations.map((organization) => [organization.id, organization.name]),
  )
}

export function DesktopAuthorizePage() {
  const auth = useAuth()
  const params =
    typeof window === 'undefined'
      ? null
      : desktopAuthorizationParams(window.location.search)
  const [status, setStatus] = useState<'idle' | 'working'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('')
  const preview = useQuery({
    queryKey: ['desktop-auth-preview', params?.attemptId],
    queryFn: () => {
      if (!params) throw new Error('missing desktop login attempt')
      return previewDesktopSession(params.attemptId, params.state)
    },
    enabled: params !== null && auth.status === 'authenticated',
    retry: false,
  })
  const needsOrganization =
    auth.status === 'authenticated' && !auth.user.active_org
  const organizations = useOrganizations(needsOrganization)
  const organizationRows = organizations.query.data ?? []
  const organizationId =
    auth.status === 'authenticated'
      ? loreOrganizationToAuthorize(
          auth.user.active_org?.id ?? null,
          selectedOrganizationId,
          organizationRows,
        )
      : null
  const personalAllowed =
    auth.status === 'authenticated' && auth.user.personal_scope === true
  const authorizationContextReady =
    !needsOrganization ||
    organizationId !== null ||
    (organizations.query.isSuccess && personalAllowed)
  const authorizationReady = authorizationContextReady && preview.isSuccess

  const login = () => {
    const returnTo = `${window.location.pathname}${window.location.search}`
    window.location.assign(`/login?return_to=${encodeURIComponent(returnTo)}`)
  }

  const authorize = async () => {
    if (!params || auth.status !== 'authenticated') return
    setStatus('working')
    setError(null)
    try {
      const response = await authorizeDesktopSession(
        params.attemptId,
        params.state,
        organizationId,
      )
      window.location.assign(response.callback_url)
    } catch (cause) {
      setStatus('idle')
      setError(
        cause instanceof Error
          ? cause.message
          : 'Nie udalo sie autoryzowac aplikacji',
      )
    }
  }

  return (
    <AuthShell>
      <div className="space-y-5 text-center" data-testid="desktop-authorize">
        <h1 className="flex items-center justify-center gap-2 text-2xl font-bold text-foreground">
          <ArchigmaMark className="h-7 w-5 text-foreground" />
          Autoryzacja aplikacji Archigma
        </h1>

        {!params && (
          <NoticeBanner
            tone="error"
            message="Nieprawidlowa lub niepelna proba logowania. Wroc do aplikacji i rozpocznij ponownie."
          />
        )}
        {error && <NoticeBanner tone="error" message={error} />}
        {preview.isError && (
          <NoticeBanner
            tone="error"
            message="Ta proba logowania wygasla, zostala juz uzyta albo nie pasuje do otwartej strony. Wroc do aplikacji i rozpocznij ponownie."
          />
        )}

        {params && auth.status === 'unauthenticated' && (
          <>
            <p className="text-sm text-muted-foreground">
              Zaloguj sie kontem Archigma, aby polaczyc aplikacje desktopowa.
            </p>
            <Button className="w-full" size="lg" onClick={login}>
              Zaloguj sie
            </Button>
          </>
        )}

        {params && auth.status === 'authenticated' && (
          <>
            <p className="text-sm text-muted-foreground">
              Laczenie jako <strong>{auth.user.email}</strong>.
            </p>
            {preview.data && (
              <div className="space-y-3 rounded-lg border p-4 text-left">
                <p className="font-medium">
                  {PRODUCT_LABELS[preview.data.product]}
                </p>
                <p className="text-sm text-muted-foreground">
                  Aplikacja prosi o:
                </p>
                <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                  {preview.data.scopes.map((scope) => (
                    <li key={scope}>{SCOPE_LABELS[scope]}</li>
                  ))}
                </ul>
              </div>
            )}
            {needsOrganization && organizations.query.isPending && (
              <p className="text-sm text-muted-foreground">
                Sprawdzanie organizacji...
              </p>
            )}
            {needsOrganization && organizations.query.isError && (
              <NoticeBanner
                tone="error"
                message="Nie udalo sie pobrac organizacji. Odswiez strone i sprobuj ponownie."
              />
            )}
            {needsOrganization && organizationRows.length > 1 && (
              <Select
                items={desktopOrganizationSelectItems(organizationRows)}
                value={selectedOrganizationId || null}
                onValueChange={(value) =>
                  setSelectedOrganizationId(value ?? '')
                }
              >
                <SelectTrigger
                  className="w-full"
                  aria-label="Organizacja aplikacji desktopowej"
                >
                  <SelectValue placeholder="Wybierz organizacje" />
                </SelectTrigger>
                <SelectContent>
                  {organizationRows.map((organization) => (
                    <SelectItem key={organization.id} value={organization.id}>
                      {organization.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {needsOrganization &&
              organizations.query.isSuccess &&
              organizationRows.length === 0 &&
              !personalAllowed && (
                <NoticeBanner
                  tone="error"
                  message="To konto nie ma dostepnego kontekstu organizacji ani dostepu osobistego."
                />
              )}
            <Button
              className="w-full"
              size="lg"
              disabled={status === 'working' || !authorizationReady}
              onClick={() => void authorize()}
            >
              {status === 'working'
                ? 'Autoryzowanie...'
                : 'Autoryzuj aplikacje'}
            </Button>
          </>
        )}

        {auth.status === 'loading' && (
          <p className="text-sm text-muted-foreground">Sprawdzanie sesji...</p>
        )}
        {auth.status === 'apiUnavailable' && (
          <NoticeBanner
            tone="error"
            message="Serwis logowania jest chwilowo niedostepny."
          />
        )}
      </div>
    </AuthShell>
  )
}
